import React, { useEffect, useState, useCallback } from 'react'
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  useTheme,
  styled,
  Paper,
} from '@mui/material'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useNavigate, useParams } from 'react-router-dom'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import ArrowIconRight from '../../Assets/Icons/Arrow_Small_Right.svg'
import PDFIcon from '../../Assets/Icons/document_icon.svg'
import IQAC_Data from '../../Assets/JSON/ActivitiesJson/SAC.json'

const SAC = () => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(0)
  const [selectedNaccData, setSelectedNaccData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const params = useParams()

  const IQACExtended_DATA = IQAC_Data?.dOutcome_Headings
  const IQACSubheadingData = IQAC_Data.dOutcomeExtended_Data

  const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
  ))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    marginBottom: theme.spacing(1),
    background: theme.palette.background.paper,
  }))

  const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  }))

  const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
      expandIcon={
        <img
          src={ArrowIconRight}
          alt="Arrow Icon"
          style={{
            width: 16,
            filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
          }}
        />
      }
      {...props}
    />
  ))(({ theme }) => ({
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
      backgroundColor: theme.palette.action.selected,
      borderRadius: '50%',
      padding: 4,
    },
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(1),
    },
  }))

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false)
    if (newExpanded) {
      const expandedPanel = document.getElementById(`accordion-panel-${panel}`)
      if (expandedPanel) {
        expandedPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }

  const filterData = useCallback(
    (selected) => {
      try {
        const filteredData = IQACSubheadingData.filter(
          (Naccdata) => Naccdata.ID === selected.ID
        )
        setSelectedNaccData(filteredData)
        navigate(`/sac`)
      } catch (err) {
        setError(err.message)
        toast.error('Failed to filter data')
      }
    },
    [IQACSubheadingData, navigate]
  )

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true)
        if (IQACSubheadingData && IQACSubheadingData.length > 0) {
          await filterData({
            ID: 11,
            Subheading: 'Programme Outcomes / Course Outcomes',
          })
        }
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    initializeData()
  }, [IQACSubheadingData, filterData])

  const handlePdfClick = (urldata) => {
    if (!urldata.pathurl?.trim()) {
      toast.error('There is no file available', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } else {
      window.open(urldata.pathurl, '_blank')
    }
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading SAC data: {error}
        </Alert>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: 'theme.palette.background.default',
        minHeight: '100vh',
      }}
    >
      <Grid container spacing={3}>
        {/* Left Panel - Accordion */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 1, borderRadius: 2 }}>
            {IQACExtended_DATA?.map((items, parentIndex) => {
              const isExpanded = expanded === parentIndex
              return (
                <Accordion
                  key={parentIndex}
                  expanded={isExpanded}
                  onChange={handleChange(parentIndex)}
                  id={`accordion-panel-${parentIndex}`}
                >
                  <AccordionSummary
                    sx={{
                      backgroundColor: isExpanded
                        ? '#2391d0'
                        : theme.palette.grey[200],
                      color: isExpanded ? 'white' : 'inherit',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: isExpanded
                          ? theme.palette.primary.dark
                          : theme.palette.grey[300],
                      },
                    }}
                    onClick={() => {
                      if (!isExpanded && items.SubHeading?.length > 0) {
                        filterData(items.SubHeading[0])
                      }
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      color={isExpanded ? 'white' : theme.palette.text.primary}
                      fontWeight="medium"
                    >
                      {items.Heading}
                    </Typography>
                  </AccordionSummary>

                  {items?.SubHeading?.map((subdata, i) => (
                    <AccordionDetails
                      key={i}
                      onClick={() => filterData(subdata)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        backgroundColor: selectedNaccData.some(
                          (data) => data.ID === subdata.ID
                        )
                          ? theme.palette.action.selected
                          : 'inherit',
                      }}
                    >
                      <Typography variant="body2" sx={{ pl: 2 }}>
                        {subdata.Subheading}
                      </Typography>
                    </AccordionDetails>
                  ))}
                </Accordion>
              )
            })}
          </Paper>
        </Grid>

        {/* Right Panel - Content */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={3}
            sx={{ p: 3, borderRadius: 2, minHeight: '100%' }}
          >
            {selectedNaccData[0]?.URLs ? (
              <Box>
                {selectedNaccData.map((HeadItem) =>
                  Object.entries(HeadItem.URLs).map(([key, value]) => (
                    <Box key={key} sx={{ mb: 4 }}>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                          fontWeight: 'bold',
                          color: theme.palette.text.primary,
                          mb: 3,
                        }}
                      >
                        {value.map((item, index) => (
                          <React.Fragment key={index}>
                            {index === 0 ? item.Title : `, ${item.Title}`}
                          </React.Fragment>
                        ))}
                      </Typography>

                      <Grid container spacing={2}>
                        {value.map((urldata, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                p: 2,
                                '&:hover': {
                                  backgroundColor: theme.palette.action.hover,
                                  borderRadius: 1,
                                },
                              }}
                              onClick={() => handlePdfClick(urldata)}
                            >
                              <img
                                src={PDFIcon}
                                alt="PDF"
                                style={{
                                  width: 48,
                                  height: 48,
                                  cursor: 'pointer',
                                  marginBottom: theme.spacing(1),
                                }}
                              />
                              <Typography
                                variant="body2"
                                align="center"
                                sx={{
                                  wordBreak: 'break-word',
                                  color: theme.palette.text.secondary,
                                }}
                              >
                                {urldata.filename}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))
                )}
              </Box>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <Typography variant="h6" color="textSecondary">
                  No data selected
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={theme.palette.mode}
      />
    </Box>
  )
}

export default SAC
